/**
 * Workflow Success Message Component
 * Simple status display for workflow dispatch - shows plain language messages
 */

import React from 'react';
import { Card, Stack, Flex, Button, useTheme, spacing, fontSize } from '@fti/design-system';

export interface WorkflowSuccessMessageProps {
  /** Whether the workflow is being dispatched */
  isDispatching?: boolean;
  /** Whether the workflow was successfully started */
  isSuccess?: boolean;
  /** Error message if dispatch failed */
  error?: string | null;
  /** Callback to reset and start a new workflow */
  onReset?: () => void;
  /** Optional callback to view workflow history */
  onViewHistory?: () => void;
}

/**
 * Displays simple workflow status messages
 * - Dispatching: "Starting workflow..."
 * - Success: "Workflow started successfully"
 * - Error: Shows error message with retry option
 */
export function WorkflowSuccessMessage({
  isDispatching = false,
  isSuccess = false,
  error = null,
  onReset,
  onViewHistory,
}: WorkflowSuccessMessageProps) {
  const { theme } = useTheme();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const successColor = theme.colors.semantic.success.default;
  const errorColor = theme.colors.semantic.error.default;

  // Show dispatching state
  if (isDispatching) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex align="center" gap="sm">
            <span style={{ fontSize: fontSize.xl }}>...</span>
            <div>
              <h3 style={{ margin: 0, color: textColor, fontSize: fontSize.base }}>
                Starting Workflow
              </h3>
              <p style={{ margin: 0, marginTop: spacing.xs, color: mutedColor, fontSize: fontSize.sm }}>
                Please wait while the workflow is being started...
              </p>
            </div>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="sm">
            <Flex align="center" gap="sm">
              <span style={{ fontSize: fontSize.xl }}>X</span>
              <h3 style={{ margin: 0, color: errorColor, fontSize: fontSize.base }}>
                Failed to Start Workflow
              </h3>
            </Flex>
            <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.sm }}>
              {error}
            </p>
            {onReset && (
              <Button variant="outline" color="primary" size="sm" onClick={onReset}>
                Try Again
              </Button>
            )}
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  // Show success state
  if (isSuccess) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="md">
            <Flex align="center" gap="sm">
              <span
                style={{
                  fontSize: fontSize.xl,
                  color: successColor,
                }}
              >
                OK
              </span>
              <div>
                <h3 style={{ margin: 0, color: successColor, fontSize: fontSize.base }}>
                  Workflow Started Successfully
                </h3>
                <p style={{ margin: 0, marginTop: spacing.xs, color: mutedColor, fontSize: fontSize.sm }}>
                  Your transcription workflow has been submitted and is now processing.
                  Results will be emailed to you upon completion and will also be available in View Transcriptions.
                </p>
              </div>
            </Flex>

            <Flex gap="sm">
              {onReset && (
                <Button variant="outline" color="primary" size="sm" onClick={onReset}>
                  Start Another
                </Button>
              )}
              {onViewHistory && (
                <Button variant="ghost" size="sm" onClick={onViewHistory}>
                  View Workflow History
                </Button>
              )}
            </Flex>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  return null;
}

export default WorkflowSuccessMessage;
